/* What the /cli-login consent screen says, per CLI.
 *
 * Its own module so the screen and its tests share one definition of the words rather
 * than each carrying a copy.
 */

import { CLI_NAME } from "./authBrand";
import type { AuthBrand } from "./authBrand";
import type { CliLoginRefusal } from "./cliLoginRequest";

/* Who the screen is talking to. `unnamed` is a CLI that sent no `client` — hopt builds
 * from before 2026-09-09, or anything hand-rolled. It gets copy that names no product:
 * guessing would put the wrong tool in front of a user whose job here is to spot one. */
export type CliAudience = AuthBrand | "unnamed";

export function audienceFrom(client: AuthBrand | null): CliAudience {
  return client ?? "unnamed";
}

export function headingFor(audience: CliAudience): string {
  return audience === "unnamed"
    ? "Sign in to the command-line tool?"
    : `Sign in to the ${CLI_NAME[audience]} CLI?`;
}

/* The `harness` strings are the originals, word for word; the `gateway` and `unnamed`
 * ones name no command, because the gateway CLI is invoked several ways and a wrong
 * invocation printed here is worse than none. Any refusal that follows a request we might
 * have acted on says plainly that nothing was shared; `incomplete` and `framed` do not,
 * because neither is reached with a credential anywhere in sight. */
export const REFUSAL_COPY: Record<
  CliAudience,
  Record<CliLoginRefusal | "framed", string>
> = {
  unnamed: {
    incomplete:
      "This link is missing the details a command-line sign-in needs. Start the sign-in again from your terminal.",
    "untrusted-callback":
      "This link asked us to send your sign-in somewhere we will not send it. Nothing has been shared. Start the sign-in again from your terminal.",
    malformed:
      "This link is not one a command-line tool produced. Nothing has been shared. Start the sign-in again from your terminal.",
    expired:
      "This sign-in request has expired — the tool that asked for it has stopped waiting. Nothing has been shared. Start the sign-in again from your terminal.",
    framed:
      "This page cannot be shown inside another page. Open it directly in a tab, or start the sign-in again from your terminal.",
  },
  gateway: {
    incomplete:
      "This link is missing the details a command-line sign-in needs. Start the sign-in again from your terminal.",
    "untrusted-callback":
      "This link asked us to send your sign-in somewhere we will not send it. Nothing has been shared. Start the sign-in again from your terminal.",
    malformed:
      "This link is not one the AI21 Gateway CLI produced. Nothing has been shared. Start the sign-in again from your terminal.",
    expired:
      "This sign-in request has expired — the AI21 Gateway CLI that asked for it has stopped waiting. Nothing has been shared. Start the sign-in again from your terminal.",
    framed:
      "This page cannot be shown inside another page. Open it directly in a tab, or start the sign-in again from your terminal.",
  },
  harness: {
    incomplete:
      "This link is missing the details a command-line sign-in needs. Start it again from your terminal with hopt login.",
    "untrusted-callback":
      "This link asked us to send your sign-in somewhere we will not send it. Nothing has been shared. Start again from your terminal with hopt login.",
    malformed:
      "This link is not one the hopt CLI produced. Nothing has been shared. Start again from your terminal with hopt login.",
    expired:
      "This sign-in request has expired — the hopt CLI that asked for it has stopped waiting. Nothing has been shared. Run hopt login again from your terminal.",
    framed:
      "This page cannot be shown inside another page. Open it directly in a tab, or start again from your terminal with hopt login.",
  },
};

/* The recovery sentence after a delivery failure — the one stage where the user is already
 * signed in, so the fix starts with signing out. */
export const FAILED_COPY: Record<CliAudience, string> = {
  unnamed:
    "Nothing was shared. Sign out and in again, then start the sign-in again from your terminal.",
  gateway:
    "Nothing was shared. Sign out and in again, then start the sign-in again from your terminal.",
  harness:
    "Nothing was shared. Sign out and in again, then run hopt login once more from your terminal.",
};

/* The second half of "If your terminal shows a different code, or …, choose Deny." The
 * harness half names its command; the others describe the act instead. */
export const NOT_YOURS_COPY: Record<CliAudience, React.ReactNode> = {
  unnamed: "you did not just start this sign-in from your terminal",
  gateway: "you did not just start this sign-in from your terminal",
  harness: (
    <>
      you did not just run <code>hopt login</code>
    </>
  ),
};
